import AirbnbIcon from 'assets/images/icons/channels/airbnb.svg?react';
import BookingIcon from 'assets/images/icons/channels/booking.svg?react';
import HvmiIcon from 'assets/images/icons/channels/hvmi.svg?react';
import VrboIcon from 'assets/images/icons/channels/vrbo.svg?react';
import useFetchAgencyChannelSettings from 'components/domain/channelSettings/useFetchAgencyChannelSettings';
import PropertyMultiSelectFilterField from 'components/forms/domain/property/PropertyMultiSelectFilterField/PropertyMultiSelectFilterField';
import SelectField from 'components/forms/selectField/SelectField';
import TextField from 'components/forms/textField/TextField';
import { Channel } from 'models/Channels';
import { Currency } from 'models/Currency';
import { Property } from 'models/Properties';
import { AppliesToLabel } from 'pages/property/common/Common.styles';
import { Col, Row } from 'react-bootstrap';
import { useTranslation } from 'react-i18next';
import AirbnbMappingTypeField from './AirbnbMappingTypeField';
import AmountTypeField from './AmountTypeField';
import AppliesToHostfullyField from './AppliesToHostfullyField';
import BookingPercentAppliesToField from './BookingPercentAppliesToField';
import IsOptionalField from './IsOptionalField';
import MappingTypeField from './MappingTypeField';
import { DividerLabel } from './PropertyFeeModal.styles';
import RollIntoRentField from './RollIntoRentField';
import ScopeField from './ScopeField';
import TaxConditionField from './TaxConditionField';
import useFieldsOptions from './useFieldsOptions';
import useFormData from './useFormData';

const PropertyFeeModalFields = ({
  propertyCurrency,
  propertyUid,
}: {
  propertyCurrency: Currency;
  propertyUid: Property['uid'];
}) => {
  const {
    AIRBNB_UNIT_TYPE_OPTIONS,
    BOOKING_OPTIONS,
    CLEANING_FEE_COLLECTION_OPTIONS,
    HVMB_OPTIONS,
    VRBO_OPTIONS,
    typeOptions,
    conditionByLengthOfStayOptions,
    conditionTypeOptions,
    amountScopeOptions,
    percentageScopeOptions,
    isAirbnbCleaningFeeCollectionVisible,
    isAirbnbUnitTypeVisible,
    conditionByNightOptions,
  } = useFieldsOptions();
  const { channelSettings } = useFetchAgencyChannelSettings();
  const { t } = useTranslation();
  const { isAmountPercentage, isTypeFee, isTypeTax } = useFormData();

  const isChannelActive = (channel: Channel) =>
    channelSettings?.some(
      ({ enumId, isActive }) => enumId === channel && isActive,
    );

  const isAirbnbActive = isChannelActive(Channel.airbnb);
  const isVrboActive = isChannelActive(Channel.homeaway);
  const isHvmbActive = isChannelActive(Channel.hvmi);
  const isBookingDotComActive = isChannelActive(Channel.booking_com);
  const isBDCPercentageFieldVisible =
    isAmountPercentage && isBookingDotComActive;

  const labelInputSizes = {
    colSmLabel: 4,
    colSmInput: 7,
  };

  return (
    <>
      <TextField
        name="name"
        label={t('common.name')}
        required
        {...labelInputSizes}
      />
      <SelectField
        name="type"
        label={t('common.type')}
        required
        {...labelInputSizes}
        options={typeOptions}
      />

      <AmountTypeField
        labelInputSizes={labelInputSizes}
        propertyCurrency={propertyCurrency}
      />

      {isTypeFee && (
        <TextField
          required
          name="fee.taxationRate"
          label={t(
            'pageProperty.feesTaxesAndPolicies.propertyFeesAndTaxes.taxRate',
          )}
          type="number"
          min={0}
          max={100}
          rightAddons={['%']}
          {...labelInputSizes}
        />
      )}

      <ScopeField
        labelInputSizes={labelInputSizes}
        amountScopeOptions={amountScopeOptions}
        percentageScopeOptions={percentageScopeOptions}
      />

      {isTypeTax && (
        <TaxConditionField
          labelInputSizes={labelInputSizes}
          conditionByLengthOfStayOptions={conditionByLengthOfStayOptions}
          conditionTypeOptions={conditionTypeOptions}
          conditionByNightOptions={conditionByNightOptions}
        />
      )}

      <Row>
        <Col xs={12} sm={labelInputSizes.colSmLabel}>
          <AppliesToLabel $required>
            {t(
              'pageProperty.feesTaxesAndPolicies.propertyFeesAndTaxes.appliesTo',
            )}
          </AppliesToLabel>
        </Col>
        <Col xs={12} sm={labelInputSizes.colSmInput}>
          <PropertyMultiSelectFilterField
            name="appliesTo"
            enableHotels={false}
            enableUnitTypes
          />
        </Col>
      </Row>

      <Row>
        <Col xs={12} sm={10} smOffset={1}>
          <DividerLabel>
            {t(
              'pageProperty.feesTaxesAndPolicies.propertyFeesAndTaxes.addModal.channelSpecific',
            )}
          </DividerLabel>
        </Col>
      </Row>

      <AppliesToHostfullyField labelInputSizes={labelInputSizes} />

      <IsOptionalField labelInputSizes={labelInputSizes} />

      <RollIntoRentField labelInputSizes={labelInputSizes} />

      {isAirbnbActive && (
        <AirbnbMappingTypeField
          labelInputSizes={labelInputSizes}
          propertyUid={propertyUid}
        />
      )}

      {isAirbnbUnitTypeVisible && (
        <MappingTypeField
          name="airbnbUnit"
          label={t(
            'pageProperty.feesTaxesAndPolicies.propertyFeesAndTaxes.addModal.airbnbFeeUnitType',
          )}
          options={AIRBNB_UNIT_TYPE_OPTIONS}
          icon={<AirbnbIcon />}
          labelInputSizes={labelInputSizes}
          channel={Channel.airbnb}
          propertyUid={propertyUid}
        />
      )}

      {isAirbnbCleaningFeeCollectionVisible && (
        <MappingTypeField
          includeEmptySelectOption={false}
          name="cleaningFeeCollection"
          label={t(
            'pageProperty.feesTaxesAndPolicies.propertyFeesAndTaxes.addModal.cleaningFeeCollection',
          )}
          options={CLEANING_FEE_COLLECTION_OPTIONS}
          icon={<AirbnbIcon />}
          labelInputSizes={labelInputSizes}
          channel={Channel.airbnb}
          propertyUid={propertyUid}
        />
      )}

      {isVrboActive && (
        <MappingTypeField
          name="vrbo"
          label={t(
            'pageProperty.feesTaxesAndPolicies.propertyFeesAndTaxes.addModal.vrboMappingType',
          )}
          options={VRBO_OPTIONS}
          icon={<VrboIcon />}
          labelInputSizes={labelInputSizes}
          channel={Channel.homeaway}
          propertyUid={propertyUid}
        />
      )}

      {isBookingDotComActive && (
        <MappingTypeField
          name="booking"
          label={t(
            'pageProperty.feesTaxesAndPolicies.propertyFeesAndTaxes.addModal.bookingMappingType',
          )}
          options={BOOKING_OPTIONS}
          icon={<BookingIcon />}
          labelInputSizes={labelInputSizes}
          channel={Channel.booking_com}
          propertyUid={propertyUid}
        />
      )}

      {isBDCPercentageFieldVisible && (
        <BookingPercentAppliesToField labelInputSizes={labelInputSizes} />
      )}

      {isHvmbActive && (
        <MappingTypeField
          name="hvmb"
          label={t(
            'pageProperty.feesTaxesAndPolicies.propertyFeesAndTaxes.addModal.hvmbMappingType',
          )}
          options={HVMB_OPTIONS}
          icon={<HvmiIcon />}
          labelInputSizes={labelInputSizes}
          channel={Channel.hvmi}
          propertyUid={propertyUid}
        />
      )}
    </>
  );
};

export default PropertyFeeModalFields;
